'use client';
import CreatePostsMenu from '@/app/(protected)/home/CreatePostsMenu';
import { cn } from '@/lib/utils';
import { cx } from 'cva';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { useEffect, useMemo, useState } from 'react';
import { BsBellFill, BsGlobeAmericas } from 'react-icons/bs';
import { GoHomeFill } from 'react-icons/go';
import { MdAddCircleOutline } from 'react-icons/md';
import Dialog from '../next-ui/Dialog';
import useHasUserSession from '@/hooks/useHasUserSession';
import useUserProfile from '@/hooks/useUserProfile';
import PostType from '@/constants/postType.constants';
import { useHomeFeedFilters } from '@/actions/feed.actions';
import Typography from '../next-ui/Typography';
import { isCompanyAdmin, isNonProfitAdmin } from '@/utils/communityRoles';
import { MdAdminPanelSettings, MdExplore } from 'react-icons/md';
import { kebabCase, last } from 'lodash-es';
import { GoHeartFill } from 'react-icons/go';
import { getAdminLoginUrl, getClientIronSession } from '@/utils/stringHelpers';

const createPostAction = {
  appName: 'createPost',
  label: {
    verb: 'Create Post',
    noun: 'Post',
  },
  icon: '/assets/images/screennav1.png',
  src: '/assets/icons/create-post.svg',
  alt: 'post',
  checkPermission: false,
};
const getNonProfitLink = (user) => {
  const activeCommunity = user.activeCommunity;
  let slug = activeCommunity.hasPublicProfile
    ? last(activeCommunity.publicProfile.slug?.split('/'))
    : null;

  if (!slug) {
    slug = `${kebabCase(activeCommunity?.name)}-${
      activeCommunity?.nonProfit?.id
    }`;
  }
  return `/organization/${slug}`;
};

const menuItems = [
  {
    id: 'home',
    label: 'Home',
    icon: <GoHomeFill className="h-6 w-6" />,
    path: '/home',
    className: 'flex lg:hidden',
  },
  {
    id: 'donate',
    label: 'Donate',
    // icon: <GoHomeFill className="h-6 w-6" />,
    path: '/home',
    className: 'flex lg:hidden',
  },
  // {
  //   id: 'globe',
  //   label: 'Globe',
  //   icon: <BsGlobeAmericas className="h-6 w-6" />,
  //   path: '/nonprofit',
  //   className: 'flex lg:hidden',
  // },
  // {
  //   id: 'volunteer',
  //   label: 'Volunteer',
  //   // icon: <GoHomeFill className="h-6 w-6" />,
  //   path: '/home',
  //   className: 'flex lg:hidden',
  // },
  {
    id: 'explore',
    label: 'Explore',
    icon: <MdExplore className="h-6 w-6" />,
    path: '/explore',
    className: 'flex lg:hidden',
  },
  {
    id: 'notification',
    label: 'To Do',
    icon: <BsBellFill className="h-6 w-6" />,
    path: '/notifications',
    className: 'flex lg:hidden',
  },
];

const postTypes = [
  {
    label: 'Donate',
    value: PostType.Campaign,
    icon: (
      <img
        src="/assets/icons/donate-filter.png"
        alt="donate"
        className={cx('h-6 w-5')}
      />
    ),
    options: [
      {
        id: 0,
        label: 'Fundraisers',
        value: 'fundraisers',
        parentLabel: 'donate',
      },
      {
        id: 1,
        label: 'In-Kind (Clothes, food, etc.)',
        value: 'inKind',
        parentLabel: 'donate',
      },
    ],
    count: null,
  },
  {
    label: 'Volunteer',
    value: PostType.Volunteering,
    icon: (
      <img
        src="/assets/icons/volunteer-filter.svg"
        alt="volunteer"
        className={cx('h-6 w-6')}
      />
    ),
    options: [
      {
        id: 0,
        label: 'In-Person',
        value: 'inPerson',
        parentLabel: 'volunteer',
      },
      {
        id: 1,
        label: 'Virtual (Online)',
        value: 'virtual',
        parentLabel: 'volunteer',
      },
    ],
    count: null,
  },
];

const getMenuItems = (user, ironSession) => {
  const isNpoAdmin = isNonProfitAdmin(user);
  const isCompAdmin = isCompanyAdmin(user);

  if (isNpoAdmin) {
    return [
      {
        id: 'home',
        label: 'Home',
        icon: <GoHomeFill className="h-6 w-6" />,
        path: '/home',
        className: 'flex lg:hidden',
      },
      // {
      //   id: 'my-nonprofit',
      //   label: 'My Nonprofit',
      //   icon: <GoHeartFill className="h-6 w-6" />,
      //   path: getNonProfitLink(user),
      //   className: 'flex lg:hidden',
      // },
      {
        id: 'explore',
        label: 'Explore',
        icon: <MdExplore className="h-6 w-6" />,
        path: '/explore',
        className: 'flex lg:hidden',
      },
      {
        id: 'admin',
        label: 'Admin',
        icon: <MdAdminPanelSettings className="h-6 w-6" />,
        path: getAdminLoginUrl(user?.email, ironSession?.accessToken),
        className: 'flex lg:hidden',
      },
      {
        id: 'notification',
        label: 'To Do',
        icon: <BsBellFill className="h-6 w-6" />,
        path: '/notifications',
        className: 'flex lg:hidden',
      },
    ];
  }

  if (isCompAdmin) {
    return [
      {
        id: 'home',
        label: 'Home',
        icon: <GoHomeFill className="h-6 w-6" />,
        path: '/home',
        className: 'flex lg:hidden',
      },
      {
        id: 'explore',
        label: 'Explore',
        icon: <MdExplore className="h-6 w-6" />,
        path: '/explore',
        className: 'flex lg:hidden',
      },
      {
        id: 'admin',
        label: 'Admin',
        icon: <MdAdminPanelSettings className="h-6 w-6" />,
        path: getAdminLoginUrl(user?.email, ironSession?.accessToken),
        className: 'flex lg:hidden',
      },
      {
        id: 'notification',
        label: 'To Do',
        icon: <BsBellFill className="h-6 w-6" />,
        path: '/notifications',
        className: 'flex lg:hidden',
      },
    ];
  }

  return menuItems;
};

const TabIcon = ({
  icon,
  label,
  path,
  isSelected,
  direction,
  className = '',
  onClick,
  user,
}) => {
  const postType = useHomeFeedFilters((state) => state.postType);
  const setPostType = useHomeFeedFilters((state) => state.setPostType);
  const resetFilters = useHomeFeedFilters((state) => state.resetFilters);
  // const unReadCount = user?.notifications?.unreadCount;
  const unReadCount = user?.todo?.total;
  const filters = ['Donate', 'Volunteer'];
  const filter = postTypes.find((p) => p.label === label);

  const handlePostTypeSelection = (type) => {
    setPostType(type);
  };

  const handleClick = (event) => {
    // event.preventDefault(); // Prevent any default actions
    onClick(); // Call the onClick handler

    if (filters.includes(label)) {
      handlePostTypeSelection(filter.value); // Handle the filter selection

      sessionStorage.setItem('home-filter', filter.value);
    } else if (label === 'Home') {
      sessionStorage.removeItem('home-filter');
    }
  };

  return (
    <Link
      passHref
      href={path}
      onClick={handleClick}
      onTouchEnd={handleClick}
      className={cn(
        'relative flex h-full cursor-pointer flex-col items-center',
        className,
      )}>
      {label === 'To Do' && unReadCount > 0 && (
        <div className="absolute right-[3%] top-[2px] z-10 flex h-5 w-6 items-center justify-center rounded-lg bg-red-600 text-[10px] text-white">
          {unReadCount >= 99 ? '99+' : unReadCount}
        </div>
      )}
      <div className="flex w-[60px] flex-1 flex-col items-center justify-center">
        <div
          className={cx(
            'flex h-8 w-8 transform items-center justify-center rounded-full p-1 text-white transition-all duration-1000 ease-in-out',
            isSelected ? 'bg-primary' : 'bg-text-disabled',
            // label !== 'Notification' && 'px-2',
          )}>
          {filter ? filter.icon : icon}
        </div>
        <Typography
          variant="h6"
          className={cx(
            'text-center text-[9px] font-normal',
            isSelected ? '' : '',
          )}>
          {label}
        </Typography>
      </div>
      {/* <Typography
        variant="h6"
        className={cx(
          'text-center text-12 font-normal',
          isSelected ? 'text-primary' : 'text-text-disabled',
        )}>
        {label}
      </Typography> */}
      <div className="relative h-[5px] w-full">
        {/* <Typography
          variant="h6"
          className={cx(
            'text-center text-12 font-normal',
            isSelected ? 'text-primary' : 'text-text-disabled',
          )}>
          {label}
        </Typography> */}
        <div
          className={cx(
            'duration-250 absolute top-0 h-full transform bg-primary ease-in',
            isSelected ? 'w-full transition-all' : 'w-0 transition-none',
            isSelected && direction === 'right' ? 'left-0' : 'right-0',
          )}></div>
      </div>
    </Link>
  );
};

function MobileNavBar() {
  const hasSession = useHasUserSession();
  const user = useUserProfile();
  const path = usePathname();
  const [ironSession, setIronSession] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [direction, setDirection] = useState('right');
  const [showPostsMenu, setShowPostsMenu] = useState(false);

  const resetFilters = useHomeFeedFilters((state) => state.resetFilters);

  const processedMenuItems = useMemo(
    () => getMenuItems(user, ironSession),
    [ironSession],
  );

  let leftItems = processedMenuItems.slice(0, 2);
  let rightItems = processedMenuItems.slice(2);

  const handlePathChange = () => {
    const tab = processedMenuItems.find((item) => path.startsWith(item.path));
    if (tab) {
      setSelectedTab(tab);
    } else {
      setSelectedTab(null);
    }
  };

  const handleFetchClientIronSession = async () => {
    const session = await getClientIronSession();
    setIronSession(session);
  };

  useEffect(() => {
    handlePathChange();
    handleFetchClientIronSession();
  }, []);

  const handleTabClick = (tab) => {
    // if (tab !== selectedTab) {
    const currentIndex = processedMenuItems.findIndex(
      (item) => item.id === selectedTab,
    );
    const nextIndex = processedMenuItems.findIndex((item) => item.id === tab);
    setDirection(nextIndex > currentIndex ? 'right' : 'left');
    setSelectedTab(tab);
    if (tab === 'home') {
      resetFilters();
    }
    // }
  };

  const handleCreatePostClick = () => {
    if (showPostsMenu) {
      handlePostsMenuClose();
    } else {
      setSelectedTab(null);
      setShowPostsMenu(true);
    }
  };

  const handlePostsMenuClose = () => {
    setShowPostsMenu(false);
    handlePathChange();
  };

  if (!hasSession) {
    return null;
  }

  return (
    <div className="sticky bottom-0 left-0 z-1000 h-[60px] w-full bg-white shadow-[0px_-8px_18px_0px_#e2e8f0] lg:hidden">
      <div className="flex h-full justify-center">
        <div className="flex h-full items-center gap-4">
          {leftItems.map((item) => (
            <TabIcon
              key={item.id}
              icon={item.icon}
              label={item.label}
              path={item.path}
              isSelected={
                selectedTab?.id === item.id || selectedTab === item.id
              }
              direction={direction}
              className={item.className}
              onClick={() => handleTabClick(item.id)}
              user={user}
            />
          ))}

          <div className="flex h-full w-[60px] cursor-pointer flex-col items-center justify-center border-b-[5px] border-transparent">
            <button
              className="flex h-8 w-8 items-center justify-center rounded-full bg-text-disabled p-1 text-white transition-all duration-1000 ease-in-out"
              onClick={handleCreatePostClick}>
              <MdAddCircleOutline className="h-6 w-6" />
            </button>
            <Typography
              variant="h6"
              className={cx('text-center text-[9px] font-normal')}>
              {'Create'}
            </Typography>
          </div>

          {rightItems.map((item) => (
            <TabIcon
              key={item.id}
              icon={item.icon}
              label={item.label}
              path={item.path}
              isSelected={
                selectedTab?.id === item.id || selectedTab === item.id
              }
              direction={direction}
              className={item.className}
              onClick={() => handleTabClick(item.id)}
              user={user}
            />
          ))}
        </div>
      </div>

      <Dialog
        isOpen={showPostsMenu}
        title="Create a new"
        maxWidth="sm"
        onClose={handlePostsMenuClose}>
        <CreatePostsMenu
          orientation="vertical"
          onOptionSelect={handlePostsMenuClose}
          onClose={handlePostsMenuClose}
          createPostAction={createPostAction}
        />
      </Dialog>
    </div>
  );
}

export default MobileNavBar;
